import React, { Component, useContext, createRef, RefObject } from 'react';
import './Analytics.scss';

import { Redirect } from 'react-router-dom';
import { SearchContext } from '../../context/SearchContext';
import { AuthorizationContext } from '../../context/AuthorizationContext';
import { MagicPixel } from '../MagicPixel';
import TopBarSearch from '../search/TopBarSearch';
import SideBarSearch from '../search/SideBarSearch';
import { urls } from '../../logic/urls';
import { connectTeamsTheme } from './../../context/connectTeamsTheme';
import { Button, mergeClasses } from '@fluentui/react-components';
import * as utils from '../../utils/misc';

import AnalyticsResult from './AnalyticsResult';

const mobileQueryWidth = 794;
const offsetCacheKey = 'analyticsOffset';

interface AnalyticsInternalProps {
    styles: any;
    authorizationContext: any;
}

interface AnalyticsInternalState {
    selectedMediaId: string;
}

class AnalyticsInternal extends Component<AnalyticsInternalProps, AnalyticsInternalState> {
    static contextType = SearchContext;
    context!: React.ContextType<typeof SearchContext>;
    timer: NodeJS.Timeout | null;
    resultBlockRef: RefObject<any>;

    constructor(props: AnalyticsInternalProps) {
        super(props);
        this.state = {
            selectedMediaId: '',
        };
        this.timer = null;
        this.resultBlockRef = createRef();
    }

    async componentDidMount() {
        const searchContext = this.context;
        await searchContext.triggerInitialisation();
        this.handleScrollPosition();
    }

    useMediaQueryWidth = (mediaWidth: number): boolean => window.innerWidth <= mediaWidth;

    handleScrollPosition = () => {
        const scrollPosition = +localStorage.getItem(offsetCacheKey)!;
        const resultBlock = this.useMediaQueryWidth(mobileQueryWidth) ? window : this.resultBlockRef?.current;

        if (scrollPosition) {
            resultBlock.scrollTo({
                top: +scrollPosition,
            });
            localStorage.removeItem(offsetCacheKey);
        }
    };

    setScrollPosOnTop = () => {
        const resultBlock = this.useMediaQueryWidth(mobileQueryWidth) ? window : this.resultBlockRef?.current;
        (resultBlock as any).style.overflow = 'hidden';
        (resultBlock as any).style.paddingRight = '3.1rem';
        this.timer = setTimeout(() => {
            resultBlock.scrollTo({
                top: 0,
            });
            (resultBlock as any).style.overflow = '';
            (resultBlock as any).style.paddingRight = '';
        }, 1000);
    };

    componentWillUnmount() {
        if (this.timer) clearTimeout(this.timer);
    }

    handleOnClick = async (mediaId: string) => {
        const currentScroll = this.useMediaQueryWidth(mobileQueryWidth)
            ? window.pageYOffset
            : this.resultBlockRef.current.scrollTop;
        localStorage.setItem(offsetCacheKey, currentScroll.toString());
        this.setState({
            selectedMediaId: mediaId,
        });
    };

    handlePageUp = () => {
        const searchContext = this.context;
        searchContext.pageUp();
        this.setScrollPosOnTop();
    };

    handlePageDown = () => {
        const searchContext = this.context;
        searchContext.pageDown();
        this.setScrollPosOnTop();
    };

    handleLoadPage = (pageNumber: number) => {
        const searchContext = this.context;
        searchContext.loadPage(pageNumber);
        this.setScrollPosOnTop();
    };

    render() {
        if (this.state.selectedMediaId !== '') {
            const mediaParameter = `mediaId=${this.state.selectedMediaId}`;
            let playUrlWithParameters = `${urls.play}?${mediaParameter}`;
            const searchQuery = this.context?.searchSettings?.query;
            const searchFields = this.context?.searchSettings?.searchFields?.join(',');
            if (searchQuery) {
                playUrlWithParameters += `&query=${encodeURIComponent(searchQuery)}&searchFields=${encodeURIComponent(searchFields)}`;
            }
            return <Redirect push to={playUrlWithParameters} />;
        }

        const searchContext = this.context;
        const logoutButtonRendered = utils.isDebugEnabled() ? (
            <header className="display--flex">
                <Button
                    appearance="primary"
                    onClick={this.props.authorizationContext.logout}
                    style={{ marginLeft: 'auto', marginRight: '20px' }}
                >
                    Log out
                </Button>
            </header>
        ) : (
            <></>
        );
        return (
            <section style={{ minHeight: '100vh' }} className={this.props.styles.theme.body}>
                {logoutButtonRendered}
                <MagicPixel debugChanged={() => this.forceUpdate()} />
                <section className="wrapper--top display--flex">
                    <div className="top-bar width-100">
                        <div className={this.props.styles.theme.container}>
                            <div className="search-panel">
                                <TopBarSearch
                                    currentPage="analytics"
                                    searchSettings={searchContext?.searchSettings}
                                    autoCompleteResult={searchContext?.autoCompleteResult}
                                    fields={searchContext?.fieldConfiguration === undefined ? [] : searchContext.fieldConfiguration.fields}
                                />
                            </div>
                        </div>
                    </div>
                </section>
                <section className="wrapper display--flex display--grid pos">
                    <div className={mergeClasses(this.props.styles.theme.container, 'panel-side-bar')}>
                        <section className="side-bar">
                            <SideBarSearch />
                        </section>
                    </div>
                    <AnalyticsResult />
                </section>
            </section>
        );
    }
}

const SearchWithTeamsTheme = connectTeamsTheme(AnalyticsInternal);

export const Analytics: React.FC = (props) => {
    const authorizationContext = useContext(AuthorizationContext);

    if (authorizationContext === undefined) {
        throw new Error('AuthorizationContext is not defined');
    }

    return (
        <SearchWithTeamsTheme
            key={authorizationContext.activeTenantId}
            authorizationContext={authorizationContext}
            {...props}
        />
    );
};
